import copyToClipboard from "copy-to-clipboard";
import { t } from "@/i18n-js/instance";
import type { Comment as CommentType } from "@/react/types/Comment";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { flaggedContent } from "@circle-react/helpers/urlHelpers";
import { useIsIframeEmbed } from "@circle-react/hooks/useIsIframeEmbed";
import { BookmarkButton } from "@circle-react-shared/Bookmarks/BookmarkButton";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { useToast } from "@circle-react-uikit/ToastV2";

interface ActionMenuProps {
  canReport: boolean;
  canReview: boolean;
  canEdit: boolean;
  canDestroy: boolean;
  canBookmark: boolean;
  isReply: boolean;
  onReportClick: () => void;
  onEditClick: () => void;
  onDeleteClick: () => void;
  onUpdate: (comment: CommentType) => void;
  comment: CommentType;
}

export const ActionMenu = ({
  comment,
  canReport,
  canReview,
  canEdit,
  canDestroy,
  canBookmark,
  isReply,
  onReportClick,
  onEditClick,
  onDeleteClick,
  onUpdate,
}: ActionMenuProps) => {
  const isIframeEmbed = useIsIframeEmbed();
  const menuItems = [];
  const { success } = useToast();

  const handleBookmarkSuccess = (newBookmarkId: number | null) => {
    const updatedComment = {
      ...comment,
      bookmark_id: newBookmarkId,
    };
    onUpdate(updatedComment);
  };

  if (canReport) {
    menuItems.push({
      key: "report",
      label: t("comments.action_menu.report.label"),
      href: "#",
      onClick: () => {
        onReportClick();
      },
    });
  }

  if (canReview) {
    menuItems.push({
      key: "review",
      label: t("comments.action_menu.review.label"),
      to: flaggedContent.index(),
      ...(isIframeEmbed ? { target: "_blank", rel: "noreferrer" } : {}),
    });
  }

  if (canEdit) {
    menuItems.push({
      key: "edit",
      label: t("comments.action_menu.edit.label"),
      onClick: () => {
        onEditClick();
      },
    });
  }

  const showUrl = comment.show_url;
  if (showUrl) {
    menuItems.push({
      key: "Copy link",
      label: t("comments.action_menu.copy_url.label"),
      href: "#",
      onClick: () => {
        copyToClipboard(showUrl);
        success(t("comments.action_menu.copy_url.url_copied"));
      },
    });
  }

  if (canDestroy) {
    menuItems.push({
      key: "delete",
      label: t("comments.action_menu.delete.label"),
      hasDanger: true,
      onClick: () => {
        onDeleteClick();
      },
    });
  }

  if (menuItems.length === 0 && !canBookmark) {
    return null;
  }

  return (
    <div
      className={classNames("post__actions--comment ml-2 md:ml-4", {
        "post__actions--reply": isReply,
      })}
      data-testid="post-comment-actions"
    >
      <Dropdown
        menuButtonEl="div"
        button={
          <IconButton
            name="20-menu-dots-horizontal"
            iconClassName="!text-default group-hover:!text-dark !w-5 !h-5"
            className="group"
            variant="secondary"
            iconSize={20}
            buttonSize={28}
            dataTestId="action-more"
            ariaLabel={t("comments.actions")}
          />
        }
      >
        <BookmarkButton
          variant="dropdown"
          bookmarkType="comment"
          record={comment}
          onSuccess={handleBookmarkSuccess}
        />
        {menuItems.map(item => {
          const { key, ...props } = item;
          return (
            <Dropdown.ItemWithLink key={key} {...props}>
              {item.label}
            </Dropdown.ItemWithLink>
          );
        })}
      </Dropdown>
    </div>
  );
};
